import { ERROR_CODES, type ErrorCode } from '@millim/shared-types';

/**
 * Translation keys for every backend error code.
 * Adding a new code in shared-types without adding it here is a TypeScript error.
 *
 * Keys live under `common.errors.byCode.*` in the catalogs.
 * This map is exhaustively type-checked: if you add an ErrorCode
 * without adding it here, the build will fail.
 */
export const ERROR_MESSAGE_KEYS: Record<ErrorCode, string> = {
  // ── Validation & Request ──────────────────────────────────────────────────
  [ERROR_CODES.VALIDATION_ERROR]: 'common.errors.byCode.validation',
  [ERROR_CODES.INVALID_BODY]: 'common.errors.byCode.invalidBody',
  [ERROR_CODES.INVALID_JSON]: 'common.errors.byCode.invalidJson',
  [ERROR_CODES.INVALID_REQUEST]: 'common.errors.byCode.invalidRequest',
  [ERROR_CODES.INVALID_PATH]: 'common.errors.byCode.invalidPath',
  [ERROR_CODES.INVALID_RANGE]: 'common.errors.byCode.invalidRange',
  [ERROR_CODES.INVALID_PAGINATION_TOKEN]: 'common.errors.byCode.invalidPaginationToken',
  [ERROR_CODES.INVALID_LIMIT]: 'common.errors.byCode.invalidLimit',
  [ERROR_CODES.INVALID_LINK_TYPE]: 'common.errors.byCode.invalidLinkType',
  [ERROR_CODES.INVALID_DATE_FORMAT]: 'common.errors.byCode.invalidDateFormat',
  [ERROR_CODES.INVALID_DIMENSION]: 'common.errors.byCode.invalidDimension',
  [ERROR_CODES.INVALID_PLAN]: 'common.errors.byCode.invalidPlan',
  [ERROR_CODES.INVALID_STATUS]: 'common.errors.byCode.invalidStatus',
  [ERROR_CODES.DISALLOWED_FIELDS]: 'common.errors.byCode.disallowedFields',
  [ERROR_CODES.MISSING_BODY]: 'common.errors.byCode.missingBody',
  [ERROR_CODES.MISSING_SHORT_ID]: 'common.errors.byCode.missingShortId',
  [ERROR_CODES.MISSING_API_KEY]: 'common.errors.byCode.missingApiKey',
  [ERROR_CODES.MISSING_TOKEN]: 'common.errors.byCode.missingToken',
  [ERROR_CODES.MISSING_USER_ID]: 'common.errors.byCode.missingUserId',
  [ERROR_CODES.MISSING_ADMIN_ID]: 'common.errors.byCode.missingAdminId',
  [ERROR_CODES.MISSING_FEEDBACK_ID]: 'common.errors.byCode.missingFeedbackId',
  [ERROR_CODES.MISSING_CAMPAIGN_ID]: 'common.errors.byCode.missingCampaignId',
  [ERROR_CODES.NO_UPDATES]: 'common.errors.byCode.noUpdates',
  [ERROR_CODES.NO_FIELDS_TO_UPDATE]: 'common.errors.byCode.noFieldsToUpdate',
  [ERROR_CODES.INVALID_IS_ACTIVE]: 'common.errors.byCode.invalidIsActive',
  [ERROR_CODES.INVALID_IS_DELETED]: 'common.errors.byCode.invalidIsDeleted',

  // ── Authentication & Authorization ────────────────────────────────────────
  [ERROR_CODES.UNAUTHORIZED]: 'common.errors.byCode.unauthorized',
  [ERROR_CODES.INVALID_TOKEN]: 'common.errors.byCode.tokenInvalid',
  [ERROR_CODES.TOKEN_EXPIRED]: 'common.errors.byCode.tokenExpired',
  [ERROR_CODES.TOKEN_ALREADY_USED]: 'common.errors.byCode.tokenAlreadyUsed',
  [ERROR_CODES.TOKEN_VERIFICATION_FAILED]: 'common.errors.byCode.tokenVerificationFailed',
  [ERROR_CODES.TOKEN_MISMATCH]: 'common.errors.byCode.tokenMismatch',
  [ERROR_CODES.INVALID_CREDENTIALS]: 'common.errors.byCode.invalidCredentials',
  [ERROR_CODES.INVALID_CURRENT_PASSWORD]: 'common.errors.byCode.invalidCurrentPassword',
  [ERROR_CODES.SAME_PASSWORD]: 'common.errors.byCode.samePassword',
  [ERROR_CODES.FORBIDDEN]: 'common.errors.byCode.forbidden',
  [ERROR_CODES.INSUFFICIENT_SCOPE]: 'common.errors.byCode.insufficientScope',
  [ERROR_CODES.API_KEY_EXPIRED]: 'common.errors.byCode.apiKeyExpired',
  [ERROR_CODES.API_KEY_REVOKED]: 'common.errors.byCode.apiKeyRevoked',
  [ERROR_CODES.INVALID_API_KEY]: 'common.errors.byCode.invalidApiKey',
  [ERROR_CODES.INVALID_SDK_KEY]: 'common.errors.byCode.invalidSdkKey',
  [ERROR_CODES.API_ACCESS_NOT_ALLOWED]: 'common.errors.byCode.apiAccessNotAllowed',

  // ── Resource Not Found ────────────────────────────────────────────────────
  [ERROR_CODES.NOT_FOUND]: 'common.errors.byCode.notFound',
  [ERROR_CODES.USER_NOT_FOUND]: 'common.errors.byCode.userNotFound',
  [ERROR_CODES.APP_NOT_FOUND]: 'common.errors.byCode.appNotFound',
  [ERROR_CODES.LINK_NOT_FOUND]: 'common.errors.byCode.linkNotFound',
  [ERROR_CODES.FEEDBACK_NOT_FOUND]: 'common.errors.byCode.feedbackNotFound',
  [ERROR_CODES.PLAN_NOT_FOUND]: 'common.errors.byCode.planNotFound',
  [ERROR_CODES.SUBSCRIPTION_NOT_FOUND]: 'common.errors.byCode.subscriptionNotFound',
  [ERROR_CODES.EXPORT_JOB_NOT_FOUND]: 'common.errors.byCode.exportJobNotFound',
  [ERROR_CODES.EXPORT_LINK_NOT_FOUND]: 'common.errors.byCode.exportLinkNotFound',
  [ERROR_CODES.CAMPAIGN_NOT_FOUND]: 'common.errors.byCode.campaignNotFound',

  // ── Link Management ──────────────────────────────────────────────────────
  [ERROR_CODES.LINK_DELETED]: 'common.errors.byCode.linkDeleted',
  [ERROR_CODES.LINK_ALREADY_DELETED]: 'common.errors.byCode.linkAlreadyDeleted',
  [ERROR_CODES.LINK_ALREADY_DISABLED]: 'common.errors.byCode.linkAlreadyDisabled',
  [ERROR_CODES.LINK_ALREADY_ENABLED]: 'common.errors.byCode.linkAlreadyEnabled',
  [ERROR_CODES.LINK_LIMIT_REACHED]: 'common.errors.byCode.linkLimitExceeded',
  [ERROR_CODES.INVALID_SHORT_ID]: 'common.errors.byCode.invalidShortId',
  [ERROR_CODES.SHORT_ID_ALREADY_EXISTS]: 'common.errors.byCode.shortIdTaken',
  [ERROR_CODES.CUSTOM_SHORT_ID_NOT_ALLOWED]: 'common.errors.byCode.customShortIdNotAllowed',
  [ERROR_CODES.FAILED_TO_GENERATE_SHORT_ID]: 'common.errors.byCode.failedToGenerateShortId',

  // ── QR Code ──────────────────────────────────────────────────────────────
  [ERROR_CODES.INVALID_FORMAT]: 'common.errors.byCode.invalidFormat',
  [ERROR_CODES.INVALID_SIZE]: 'common.errors.byCode.invalidSize',
  [ERROR_CODES.INVALID_FG_COLOR]: 'common.errors.byCode.invalidFgColor',
  [ERROR_CODES.INVALID_BG_COLOR]: 'common.errors.byCode.invalidBgColor',

  // ── Features & Plans ─────────────────────────────────────────────────────
  [ERROR_CODES.FEATURE_NOT_AVAILABLE]: 'common.errors.byCode.featureNotAvailable',
  [ERROR_CODES.FEATURE_NOT_ALLOWED]: 'common.errors.byCode.featureNotAllowed',
  [ERROR_CODES.ADVANCED_DASHBOARD_NOT_ALLOWED]: 'common.errors.byCode.advancedDashboardNotAllowed',
  [ERROR_CODES.INSTALL_TRACKING_NOT_ALLOWED]: 'common.errors.byCode.installTrackingNotAllowed',
  [ERROR_CODES.CHANNEL_NOT_ALLOWED]: 'common.errors.byCode.channelNotAllowed',
  [ERROR_CODES.MULTI_CHANNEL_NOT_ALLOWED]: 'common.errors.byCode.multiChannelNotAllowed',
  [ERROR_CODES.SUBSCRIPTION_NOT_ACTIVE]: 'common.errors.byCode.subscriptionNotActive',
  [ERROR_CODES.NO_ACTIVE_SUBSCRIPTION]: 'common.errors.byCode.noActiveSubscription',
  [ERROR_CODES.CANNOT_CANCEL_FREE]: 'common.errors.byCode.cannotCancelFree',
  [ERROR_CODES.CANNOT_QUEUE_ON_FREE]: 'common.errors.byCode.cannotQueueOnFree',
  [ERROR_CODES.ALREADY_ON_FREE]: 'common.errors.byCode.alreadyOnFree',

  // ── User & Admin ─────────────────────────────────────────────────────────
  [ERROR_CODES.USER_DELETED]: 'common.errors.byCode.userDeleted',
  [ERROR_CODES.USER_DISABLED]: 'common.errors.byCode.userDisabled',
  [ERROR_CODES.USER_ALREADY_DELETED]: 'common.errors.byCode.userAlreadyDeleted',
  [ERROR_CODES.ALREADY_VERIFIED]: 'common.errors.byCode.alreadyVerified',
  [ERROR_CODES.EMAIL_ALREADY_VERIFIED]: 'common.errors.byCode.emailAlreadyVerified',
  [ERROR_CODES.EMAIL_NOT_VERIFIED]: 'common.errors.byCode.emailNotVerified',
  [ERROR_CODES.ALREADY_DISABLED]: 'common.errors.byCode.alreadyDisabled',
  [ERROR_CODES.NOT_DISABLED]: 'common.errors.byCode.notDisabled',
  [ERROR_CODES.SELF_DELETE_FORBIDDEN]: 'common.errors.byCode.selfDeleteForbidden',
  [ERROR_CODES.SELF_DISABLE_FORBIDDEN]: 'common.errors.byCode.selfDisableForbidden',
  [ERROR_CODES.SELF_ENABLE_FORBIDDEN]: 'common.errors.byCode.selfEnableForbidden',

  // ── Email ────────────────────────────────────────────────────────────────
  [ERROR_CODES.EMAIL_EXISTS]: 'common.errors.byCode.emailAlreadyRegistered',
  [ERROR_CODES.EMAIL_SEND_FAILED]: 'common.errors.byCode.emailSendFailed',

  // ── Campaign ─────────────────────────────────────────────────────────────
  [ERROR_CODES.CAMPAIGN_ARCHIVED]: 'common.errors.byCode.campaignArchived',

  // ── Feedback ─────────────────────────────────────────────────────────────
  [ERROR_CODES.FEEDBACK_LOCKED]: 'common.errors.byCode.feedbackLocked',

  // ── Exports ──────────────────────────────────────────────────────────────
  [ERROR_CODES.EXPORT_DATASET_UNSUPPORTED]: 'common.errors.byCode.exportDatasetUnsupported',
  [ERROR_CODES.DATA_EXPORT_NOT_ALLOWED]: 'common.errors.byCode.dataExportNotAllowed',
  [ERROR_CODES.EXPORT_DATE_RANGE_TOO_LARGE]: 'common.errors.byCode.exportDateRangeTooLarge',
  [ERROR_CODES.EXPORT_DATE_RANGE_INVALID]: 'common.errors.byCode.exportDateRangeInvalid',
  [ERROR_CODES.EXPORT_QUOTA_EXCEEDED]: 'common.errors.byCode.exportQuotaExceeded',
  [ERROR_CODES.EXPORT_CONCURRENCY_EXCEEDED]: 'common.errors.byCode.exportConcurrencyExceeded',
  [ERROR_CODES.EXPORT_ENQUEUE_FAILED]: 'common.errors.byCode.exportEnqueueFailed',

  // ── SDK/App ──────────────────────────────────────────────────────────────
  [ERROR_CODES.PLATFORM_MISMATCH]: 'common.errors.byCode.platformMismatch',
  [ERROR_CODES.PLATFORM_NOT_DECLARED]: 'common.errors.byCode.platformNotDeclared',
  [ERROR_CODES.SDK_KEY_LIMIT_REACHED]: 'common.errors.byCode.sdkKeyLimitReached',

  // ── Subscription ─────────────────────────────────────────────────────────
  [ERROR_CODES.SUBSCRIPTION_DATE_OVERLAP]: 'common.errors.byCode.subscriptionDateOverlap',
  [ERROR_CODES.NOT_QUEUED]: 'common.errors.byCode.notQueued',

  // ── Generic Errors ──────────────────────────────────────────────────────
  [ERROR_CODES.BAD_REQUEST]: 'common.errors.byCode.badRequest',
  [ERROR_CODES.CONFLICT]: 'common.errors.byCode.conflict',
  [ERROR_CODES.INTERNAL_ERROR]: 'common.errors.byCode.internal',

  // ── Rate Limiting ────────────────────────────────────────────────────────
  [ERROR_CODES.RATE_LIMIT_EXCEEDED]: 'common.errors.byCode.rateLimited',

  // ── Beta ─────────────────────────────────────────────────────────────────
  [ERROR_CODES.BETA_FEATURE_DISABLED]: 'common.errors.byCode.betaDisabled',
};

/**
 * Resolve a translation key for any error response. If the code isn't in the
 * map (legacy or missing entry), falls back to the generic 'unexpected' key.
 */
export const errorMessageKey = (code: string | undefined | null): string => {
  if (!code) return 'common.errors.unexpected';
  return (ERROR_MESSAGE_KEYS as Record<string, string>)[code] ?? 'common.errors.unexpected';
};

/**
 * Extract error code from an HTTP error response.
 * Handles multiple response shapes for flexibility.
 */
export const extractErrorCode = (error: unknown): string | undefined => {
  if (error && typeof error === 'object') {
    // Handle HttpErrorResponse shape: error.error?.error?.code
    const typedError = error as { error?: { error?: { code?: string }; code?: string } };
    if (typedError.error?.error?.code) {
      return typedError.error.error.code;
    }
    if (typedError.error?.code) {
      return typedError.error.code;
    }
  }
  return undefined;
};

/**
 * Extract user-visible message from an HTTP error response.
 * Falls back to the error code's translation key if no message is available.
 */
export const extractErrorMessage = (error: unknown): string | undefined => {
  if (error && typeof error === 'object') {
    // Try to extract message from nested structure
    const typedError = error as {
      error?: { error?: { message?: string }; message?: string };
      message?: string;
    };
    if (typedError.error?.error?.message) {
      return typedError.error.error.message;
    }
    if (typedError.error?.message) {
      return typedError.error.message;
    }
    if (typedError.message) {
      return typedError.message;
    }
  }
  return undefined;
};
